import { WEB_PAGES } from '@praxadon/shared/pages';
import { Button, Field, Input, Select } from '@praxadon/ui';
import { useQuery } from '@tanstack/react-query';
import { type SubmitEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { ApiError, describeError } from '../../api/client.ts';
import { onboarding } from '../../api/endpoints.ts';
import { useSession } from '../../app/session.tsx';
import { AuthLayout, FormError } from './AuthLayout.tsx';
import { PasswordInput, passwordHint } from './PasswordInput.tsx';
import { Turnstile, type TurnstileHandle } from './Turnstile.tsx';
import { UsernameField } from './UsernameField.tsx';

/** Self-enrolment, in whichever mode the instructor chose: open, class code or approval. */
export function SignupPage() {
  const { site, refresh } = useSession();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [token, setToken] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [suggestions, setSuggestions] = useState<string[] | undefined>();
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const turnstile = useRef<TurnstileHandle>(null);
  // The class's own questions. Asked for before the early returns below: a hook that
  // runs only sometimes is not a hook.
  const extra = useQuery({ queryKey: ['signup', 'form'], queryFn: () => onboarding.form() });
  const questions = extra.data?.questions ?? [];

  if (site.kind !== 'tenant') return null;
  const mode = site.tenant.onboardingMode;

  if (mode === 'off') {
    return (
      <AuthLayout
        form={false}
        file="sign-up.html"
        title="Joining this class"
        subtitle="This class adds students by invitation. Ask your instructor to send you one."
        footer={
          <Link className="underline hover:text-fg" to={WEB_PAGES.login}>
            Back to sign in
          </Link>
        }
      >
        <p className="text-center text-sm text-fg-muted">
          Already have an account? Sign in instead.
        </p>
      </AuthLayout>
    );
  }

  if (pendingMessage) {
    return (
      <AuthLayout form={false} file="sign-up.html" title="Almost there!" subtitle={pendingMessage}>
        <p className="text-center text-sm text-fg-muted">
          We'll email you when your account is approved
          {email ? '' : ' — or just try signing in later'}.
        </p>
      </AuthLayout>
    );
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    setSuggestions(undefined);
    try {
      const result = await onboarding.signup({
        username,
        displayName,
        password,
        ...(email ? { email } : {}),
        ...(mode === 'invite' ? { inviteCode } : {}),
        ...(Object.keys(answers).length > 0 ? { answers } : {}),
        turnstileToken: token,
      });
      if (result.status === 'pending') {
        setPendingMessage(result.message);
        return;
      }
      await refresh();
      void navigate('/', { replace: true });
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0) {
        setFields(failure.fields);
        const offered = failure.details['suggestions'];
        if (Array.isArray(offered))
          setSuggestions(offered.filter((s): s is string => typeof s === 'string'));
      } else {
        setError(describeError(failure));
      }
      turnstile.current?.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      file="sign-up.html"
      title="Create your account"
      subtitle={
        mode === 'approval'
          ? 'Your instructor will approve your account before you can sign in.'
          : 'It takes a minute, and then you can start building.'
      }
      footer={
        <>
          Already have an account?{' '}
          <Link className="font-medium text-fg underline" to={WEB_PAGES.login}>
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={error} />
        <Field
          label="Your name"
          hint="How your instructor will see you."
          error={fields['displayName']}
        >
          <Input
            name="name"
            autoComplete="name"
            maxLength={80}
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
            }}
          />
        </Field>
        <UsernameField
          value={username}
          onChange={(value) => {
            setUsername(value);
            setFields(({ username: _, ...rest }) => rest);
            setSuggestions(undefined);
          }}
          serverError={fields['username']}
          suggestions={suggestions}
        />
        {mode === 'invite' ? (
          <Field
            label="Class code"
            hint="Your instructor will give you this code."
            error={fields['inviteCode']}
          >
            <Input
              name="class-code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={inviteCode}
              onChange={(event) => {
                setInviteCode(event.target.value);
              }}
              className="font-mono tracking-widest uppercase"
            />
          </Field>
        ) : null}
        <Field
          label="Email (optional)"
          hint="Only used if you forget your password."
          error={fields['email']}
        >
          <Input
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </Field>
        <Field label="Password" hint={passwordHint(password)} error={fields['password']}>
          <PasswordInput
            name="new-password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Field>
        {questions.map((question) => (
          <Field
            key={question.id}
            label={question.required === true ? question.label : `${question.label} (optional)`}
            error={fields[`answer:${question.id}`]}
          >
            {question.kind === 'choice' ? (
              <Select
                value={answers[question.id] ?? ''}
                onChange={(event) => {
                  setAnswers({ ...answers, [question.id]: event.target.value });
                }}
              >
                <option value="">Choose one…</option>
                {(question.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                maxLength={500}
                value={answers[question.id] ?? ''}
                onChange={(event) => {
                  setAnswers({ ...answers, [question.id]: event.target.value });
                }}
              />
            )}
          </Field>
        ))}
        {site.turnstileSiteKey ? (
          <Turnstile ref={turnstile} siteKey={site.turnstileSiteKey} onToken={setToken} />
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={
            !displayName ||
            !username ||
            !password ||
            questions.some((question) => question.required === true && !answers[question.id]) ||
            (site.turnstileSiteKey !== null && !token)
          }
        >
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
